import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Seal } from "@/components/brand/Seal";
import { PrimaryButton } from "@/components/brand/PrimaryButton";

interface Row { id: string; amount: number; period_start: string | null; period_end: string | null; paid_at: string | null; notes: string | null; }

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const Route = createFileRoute("/agent/commissions")({
  head: () => ({ meta: [{ title: "Commissions — Strategist Portal" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <AgentCommissions />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AgentCommissions() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => { if (user) void load(); }, [user]);
  async function load() {
    const { data } = await supabase.from("commissions").select("id, amount, period_start, period_end, paid_at, notes").eq("agent_id", user!.id).order("created_at", { ascending: false });
    setRows((data ?? []) as Row[]);
  }

  const totals = useMemo(() => {
    const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
    let mtd = 0, ytd = 0, paid = 0, pending = 0;
    for (const r of rows) {
      const amt = Number(r.amount) || 0;
      const ref = r.paid_at ? new Date(r.paid_at) : r.period_end ? new Date(r.period_end) : null;
      if (ref && ref.getFullYear() === y) { ytd += amt; if (ref.getMonth() === m) mtd += amt; }
      if (r.paid_at) paid += amt; else pending += amt;
    }
    return { mtd, ytd, paid, pending };
  }, [rows]);

  function exportCsv() {
    const header = ["Period Start", "Period End", "Amount", "Paid", "Notes"];
    const lines = rows.map(r => [r.period_start ?? "", r.period_end ?? "", r.amount, r.paid_at ?? "", r.notes ?? ""].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `commissions-${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
  }

  return (
    <PortalPage eyebrow="Earnings" title="Commissions" intro="Your statements, summary, and payout history." actions={<PrimaryButton onClick={exportCsv}>Export CSV</PrimaryButton>}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Seal title={fmt(totals.mtd)} subtitle="Month to date" size="sm" />
        <Seal title={fmt(totals.ytd)} subtitle="Year to date" size="sm" />
        <Seal title={fmt(totals.paid)} subtitle="Paid" size="sm" />
        <Seal title={fmt(totals.pending)} subtitle="Pending" size="sm" />
      </div>
      {rows.length === 0 ? (
        <div className="bg-cream-50 border border-bronze-600/30 rounded-[4px] p-10 text-center">
          <h3 className="font-display text-navy-800 text-[22px]">No statements yet</h3>
          <p className="mt-2 text-charcoal-500 text-[14px]">Once policies you write begin paying commissions, your statements appear here.</p>
        </div>
      ) : (
        <div className="bg-white border border-bronze-600/20 rounded-[4px] overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-left text-charcoal-500 font-sans uppercase tracking-[0.12em] text-[10px]">
              <tr className="border-b border-bronze-600/20"><th className="px-4 py-3">Period</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Paid</th><th className="px-4 py-3">Notes</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-bronze-600/10">
                  <td className="px-4 py-3 text-navy-800">{r.period_start ? new Date(r.period_start).toLocaleDateString() : "—"} – {r.period_end ? new Date(r.period_end).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 font-medium">{fmt(Number(r.amount) || 0)}</td>
                  <td className="px-4 py-3 text-charcoal-500">{r.paid_at ? new Date(r.paid_at).toLocaleDateString() : "Pending"}</td>
                  <td className="px-4 py-3 text-charcoal-500">{r.notes ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PortalPage>
  );
}
